import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DEFAULT_APP_ICON, isAppIconSrc } from '@/lib/appIcon'
import { activityHeadline } from './activityTitle'
import { activityTimeLabel } from './activityTime'
import type { ActivityItem } from './types'

const toneClass: Record<NonNullable<ActivityItem['tone']>, string> = {
  default: 'text-black/60',
  danger: 'text-[#FF383C]',
  success: 'text-[#00B42A]',
}

function ActivityIcon({ icon, box }: { icon: string; box: string }) {
  const [failed, setFailed] = useState(false)
  const src = !failed && isAppIconSrc(icon) ? icon : DEFAULT_APP_ICON

  return (
    <div
      className={`flex shrink-0 items-center justify-center overflow-hidden border border-[#DCDCDC] bg-white ${box}`}
    >
      <img
        src={src}
        alt=""
        className="h-full w-full object-cover"
        onError={() => {
          if (src !== DEFAULT_APP_ICON) setFailed(true)
        }}
      />
    </div>
  )
}

function ActivityTitle({
  title,
  appName,
  className,
}: {
  title: string
  appName: string
  className: string
}) {
  const text = activityHeadline(title, appName)
  return (
    <span className={`block min-w-0 truncate ${className}`} title={text}>
      {text}
    </span>
  )
}

function useActivityTimeText(iso: string): string | null {
  const { t, i18n } = useTranslation()
  const label = activityTimeLabel(iso, new Date(), i18n.language)
  if (!label) return null
  if (label.kind === 'today') return t('workbench.activityToday', { time: label.time })
  if (label.kind === 'yesterday') {
    return t('workbench.activityYesterday', { time: label.time })
  }
  return label.text
}

interface ActivityRowProps {
  item: ActivityItem
  compact?: boolean
  onClick?: () => void
}

export function ActivityRow({ item, compact = true, onClick }: ActivityRowProps) {
  const time = useActivityTimeText(item.created_at)
  const tone = toneClass[item.tone ?? 'default']

  const body = compact ? (
    <>
      <ActivityIcon icon={item.appIcon} box="h-[42px] w-[42px] rounded-lg" />
      <div className="flex min-w-0 flex-1 flex-col justify-center">
        <div className="flex min-w-0 items-center gap-5">
          <span
            className="min-w-0 shrink truncate text-sm leading-[22px] font-normal text-black"
            title={item.appName}
          >
            {item.appName}
          </span>
          <ActivityTitle
            title={item.title}
            appName={item.appName}
            className="shrink-0 text-sm leading-[22px] font-normal text-black"
          />
        </div>
        <span className={`truncate text-xs leading-[18px] font-normal ${tone}`} title={item.description}>
          {item.description}
        </span>
      </div>
      {time ? (
        <div className="flex h-11 shrink-0 items-center">
          <span className="text-[13px] leading-5 font-normal text-black">{time}</span>
        </div>
      ) : null}
    </>
  ) : (
    <div className="flex min-w-0 gap-4 p-2.5">
      <ActivityIcon
        icon={item.appIcon}
        box="h-[54px] w-[54px] rounded-[14px]"
      />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex min-w-0 items-center gap-2.5">
          <span
            className="min-w-0 flex-1 truncate text-base leading-6 font-medium text-black"
            title={item.appName}
          >
            {item.appName}
          </span>
          {time ? (
            <span className="shrink-0 text-[13px] leading-5 font-normal text-black">
              {time}
            </span>
          ) : null}
        </div>
        <div className="flex min-w-0 flex-col">
          <ActivityTitle
            title={item.title}
            appName={item.appName}
            className="text-sm leading-[22px] font-normal text-black"
          />
          <span className={`min-w-0 truncate text-[13px] leading-5 font-normal ${tone}`} title={item.description}>
            {item.description}
          </span>
        </div>
      </div>
    </div>
  )

  if (compact) {
    const rowClass =
      'flex w-full min-w-0 items-center gap-5 overflow-hidden border-b border-[#DCDCDC] p-3 text-left last:border-b-0'
    if (!onClick) {
      return <div className={rowClass}>{body}</div>
    }
    return (
      <button type="button" onClick={onClick} className={`${rowClass} cursor-pointer`}>
        {body}
      </button>
    )
  }

  const cardClass =
    'w-full min-w-0 overflow-hidden rounded-[20px] border border-[#DCDCDC] bg-white/85 p-2.5 text-left shadow-[2px_2px_30px_0_rgba(0,0,0,0.05)]'

  if (!onClick) {
    return <div className={cardClass}>{body}</div>
  }

  return (
    <button type="button" onClick={onClick} className={`${cardClass} cursor-pointer`}>
      {body}
    </button>
  )
}
